// src/components/organisms/landing/section-faqs/section-faqs.tsx
"use client";

import { useMemo, useState } from "react";
import { Search, Mail, MessageCircle, FileText } from "lucide-react";
import { Accordion } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { EyebrowChip } from "@/components/atoms/landing/eyebrow-chip";
import {
  FaqFilterTabs,
  type FaqCategory,
} from "@/components/molecules/landing/faq-filter-tabs";
import { FaqItem } from "@/components/molecules/landing/faq-item";
// Default content + types live in the non-client data module so server code
// (lib/homepage.tsx) can import the real values across the RSC boundary.
import { defaultFaqsContent, type FaqsContent } from "./faqs-data";

export type { FaqsContent } from "./faqs-data";

const iconMap = {
  email: <Mail aria-hidden className="size-5" />,
  telegram: <MessageCircle aria-hidden className="size-5" />,
  docs: <FileText aria-hidden className="size-5" />,
};

export function SectionFaqs({
  content = defaultFaqsContent,
}: {
  content?: FaqsContent;
}) {
  const { eyebrow, title, description, faqs, categories, contactPrompt, contactCtas } =
    content;
  const [active, setActive] = useState<FaqCategory>("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs.filter((f) => {
      const matchCategory = active === "All" || f.category === active;
      const matchQuery =
        q.length === 0 ||
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q);
      return matchCategory && matchQuery;
    });
  }, [active, query, faqs]);

  return (
    <section
      aria-labelledby="faqs-title"
      className="bg-white py-20 px-3 sm:py-24 sm:px-4 lg:px-6"
    >
      <div className="mx-auto grid w-full max-w-[1896px] grid-cols-1 gap-10 lg:grid-cols-[1fr_2fr] lg:gap-16">
        <div className="flex flex-col gap-6">
          {eyebrow ? (
            <EyebrowChip tone="mint" className="self-start">
              {eyebrow}
            </EyebrowChip>
          ) : null}
          <h2
            id="faqs-title"
            className="text-balance font-display font-bold tracking-tight text-brand-ink text-[clamp(2rem,3.4vw,48px)] leading-[1.1]"
          >
            {title}
          </h2>
          {description ? (
            <p className="text-base text-brand-ink/70 sm:text-lg">
              {description}
            </p>
          ) : null}
          {contactCtas && contactCtas.length > 0 ? (
            <div className="flex flex-col gap-4 pt-2">
              {contactPrompt ? (
                <p className="text-sm font-medium text-brand-ink/80">
                  {contactPrompt}
                </p>
              ) : null}
              <div className="grid grid-cols-3 gap-3 max-w-sm">
                {contactCtas.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    {...(c.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border border-brand-border bg-white text-brand-ink transition-colors hover:bg-brand-surface"
                  >
                    <span
                      aria-hidden
                      className="grid size-10 place-items-center rounded-full bg-brand-muted text-brand"
                    >
                      {iconMap[c.icon]}
                    </span>
                    <span className="text-xs font-medium">{c.label}</span>
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          <label className="relative block">
            <span className="sr-only">Search FAQs</span>
            <Search
              aria-hidden
              className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-brand-ink/50"
            />
            <Input
              type="search"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-12 rounded-full border-brand-border bg-white pl-11 text-base"
              aria-controls="faqs-results"
            />
          </label>
          <FaqFilterTabs
            active={active}
            onChange={setActive}
            options={categories}
          />
          <div id="faqs-results" aria-live="polite" className="mt-2">
            {filtered.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-brand-border bg-brand-surface p-8 text-center text-sm text-brand-ink/70">
                No questions match your search.
              </p>
            ) : (
              <Accordion type="single" collapsible className="flex flex-col gap-3">
                {filtered.map((entry) => (
                  <FaqItem key={entry.id} entry={entry} />
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
